import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { CONTACTS, SERVICES } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full bg-[#0A0A0B] border border-white/10 text-white text-sm px-4 py-3 placeholder:text-neutral-600 transition-all duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata! Ti ricontattiamo entro 2 ore lavorative.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Invio non riuscito. Chiamaci direttamente al 06 83932445.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="relative py-24 lg:py-32 bg-[#0A0A0B]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14"
        >
          <p className="font-mono-tac text-xs tracking-[0.3em] text-[#FFD500] uppercase font-bold mb-4">[ 03 — Parliamone ]</p>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] text-white" data-testid="contact-title">
            Richiedi un <span className="text-[#FFD500]">Preventivo</span>
          </h2>
          <p className="mt-4 text-neutral-400 max-w-xl">
            Rispondiamo entro 2 ore lavorative. Per emergenze chiama direttamente i numeri H24.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-2 flex flex-col gap-6"
          >
            {[CONTACTS.sede, CONTACTS.diretto].map((c) => (
              <a
                key={c.number}
                href={c.href}
                data-testid={`contact-phone-${c.label.includes("Diretto") ? "diretto" : "sede"}`}
                className="group border border-white/10 p-6 hover:border-[#FFD500]/50 transition-colors duration-500"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Phone size={16} className="text-[#FFD500]" />
                  <span className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500">{c.label}</span>
                </div>
                <p className="font-display font-black text-2xl sm:text-3xl text-white group-hover:text-[#FFD500] transition-colors duration-300 tracking-wide">
                  {c.number}
                </p>
              </a>
            ))}

            <a
              href={`mailto:${CONTACTS.email}`}
              data-testid="contact-email-link"
              className="group border border-white/10 p-6 hover:border-[#FFD500]/50 transition-colors duration-500"
            >
              <div className="flex items-center gap-3 mb-2">
                <Mail size={16} className="text-[#FFD500]" />
                <span className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500">Email</span>
              </div>
              <p className="font-display font-bold text-xl text-white group-hover:text-[#FFD500] transition-colors duration-300">
                {CONTACTS.email}
              </p>
            </a>

            <div className="border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin size={16} className="text-[#FFD500]" />
                <span className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500">Le Nostre Sedi</span>
              </div>
              {CONTACTS.addresses.map((a) => (
                <div key={a.city} className="mb-3 last:mb-0" data-testid={`contact-address-${a.city.toLowerCase()}`}>
                  <p className="font-display font-bold uppercase text-[#FFD500] text-sm tracking-widest">{a.city}</p>
                  <p className="text-sm text-neutral-400">{a.address}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            id="modulo-richiesta"
            onSubmit={submit}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 bg-[#121215] border border-white/10 p-6 sm:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Nome e Cognome *</label>
                <input required value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={inputCls} data-testid="contact-input-name" />
              </div>
              <div>
                <label className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Telefono *</label>
                <input required type="tel" value={form.phone} onChange={set("phone")} placeholder="333 0000000" className={inputCls} data-testid="contact-input-phone" />
              </div>
              <div>
                <label className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Email</label>
                <input type="email" value={form.email} onChange={set("email")} placeholder="mario@email.it" className={inputCls} data-testid="contact-input-email" />
              </div>
              <div>
                <label className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Servizio Richiesto *</label>
                <select required value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`} data-testid="contact-select-service">
                  <option value="" disabled>Seleziona un servizio</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                  <option value="Altro / Emergenza H24">Altro / Emergenza H24</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="font-mono-tac text-[10px] tracking-[0.2em] uppercase text-neutral-500 block mb-2">Dettagli della richiesta *</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Descrivi il lavoro, l'indirizzo del cantiere e le tempistiche..."
                  className={`${inputCls} resize-none`}
                  data-testid="contact-input-message"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#FFD500] text-black font-display font-bold uppercase tracking-wide text-lg px-10 py-4 hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
              {sending ? "Invio in corso..." : "Invia Richiesta"}
            </button>
            <p className="mt-4 font-mono-tac text-[10px] tracking-widest text-neutral-600 uppercase">
              Inviando accetti di essere ricontattato da Peluso Service.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
